/**
 * Game constants extracted from Game.js and related files
 * DO NOT MODIFY without testing gameplay!
 * 
 * Source line numbers included for reference
 */

export const GameConstants = {
    // === DEBUG ===
    SHOW_COLLISION_DEBUG: false,  // Set to true to show collision boxes/spheres
    
    // === SHIP PHYSICS ===
    
    // From Game.js line 573-574
    SHIP_ROTATION_SPEED: 0.11,
    
    // From Game.js line 577
    SHIP_ACCELERATION: 0.006,
    
    // From Game.js line 600
    SHIP_MAX_SPEED: 3.0,
    
    // From renderShip() line 621
    SHIP_FORWARD_ACCEL: 0.03,
    
    // From Game.js line 587-588
    SHIP_FRICTION_NORMAL: 0.98,
    SHIP_FRICTION_BRAKE: 0.9,
    
    // From Game.js line 591-596 (strafe)
    SHIP_STRAFE_ACCEL: 0.03,
    
    // From renderShip() line 626-628
    SHIP_VEL_MAX_MAGNITUDE: 0.2,
    
    // Ship collision box - MEASURED from actual ship mesh geometry
    // Previous hardcoded values (1.5, 20, 2.8) were WRONG - almost double the actual size!
    // Measured from Box3().setFromObject(ship.mesh):
    // - Actual width (X): 1.512 → halfWidth: 0.756
    // - Actual height (Y): 0.741 → halfHeight: 0.371 (not used in 2D)
    // - Actual depth (Z): 3.291 → halfDepth: 1.646
    // - Center offset: (0.022, 0.001, -0.252) - mesh is shifted backward from origin
    // X = width (left-right), Z = length (front-back)
    SHIP_COLLISION_BOX: {
        halfWidth: 0.756,   // Full width = 1.512 (MEASURED from mesh)
        halfLength: 1.646,  // Full length = 3.291 (MEASURED from mesh)
        centerOffsetX: 0.022,   // Mesh center X offset from ship position
        centerOffsetY: -0.252,  // Mesh center Z offset from ship position (Y in 2D)
        collisionTolerance: 0.15  // Require this much overlap before collision (matches THREE.js OBB behavior)
    },
    SHIP_MAX_HEALTH: 200,  // From Game.js line 113
    
    // === BULLET PHYSICS ===
    
    // From Ship.js line 124-128
    BULLET_COOLDOWN: 6,  // frames
    
    // From renderBullets() line 1091
    BULLET_SPEED: 0.5,
    
    // From checkBulletAsteroidCollisions() line 805
    BULLET_DAMAGE: 10,
    
    // Estimated spawn offset (need to verify)
    BULLET_SPAWN_OFFSET: 10,
    
    // BULLET COLLISION BOX - MEASURED from original game
    // Original uses Box3 collision (not circle!)
    // From collision logs: width ~0.79-0.84, height ~0.24, depth ~0.49-0.80
    // Using average values for consistency
    BULLET_COLLISION_BOX: {
        halfWidth: 0.4,    // Half of ~0.8 width (X dimension)
        halfHeight: 0.12,  // Half of ~0.24 height (Y dimension) 
        halfDepth: 0.35    // Half of ~0.7 depth (Z dimension)
    },
    
    // === ASTEROID PHYSICS ===
    
    // From Game.js line 712
    ASTEROID_MIN_COUNT: 7,
    ASTEROID_SPAWN_INCREMENT_PER_1000_SCORE: 1,
    
    // ASTEROID_TYPES - ALL RADII MEASURED from original game
    ASTEROID_TYPES: {
        boss: {
            health: 250,
            damage: 50,
            score: 250,
            radius: 3.0  // MEASURED from original game
        },
        b: {  // big
            health: 50,
            damage: 25,
            score: 50,
            radius: 1.2  // MEASURED from original game
        },
        m: {  // medium
            health: 20,
            damage: 15,
            score: 20,
            radius: 0.9  // MEASURED from original game
        },
        s: {  // small
            health: 10,
            damage: 10,
            score: 10,
            radius: 0.5  // MEASURED from original game
        }
    },
    
    // From Game.js line 745
    ASTEROID_SPEED_BASE: 0.04,
    ASTEROID_SPEED_RANDOM: 0.08,
    ASTEROID_SPEED_SCORE_FACTOR: 1/75000,  // score / 75000
    
    // From Asteroid.js line 17
    ASTEROID_ROTATION_MIN: 0.005,
    ASTEROID_ROTATION_RANGE: 0.06,
    
    // From Asteroid.js line 11
    ASTEROID_SCALE_MIN: 3,
    ASTEROID_SCALE_MAX: 4,
    
    // === COLLECTIBLES ===
    
    // From Game.js line 810
    // Math.round(Math.random() * 20) === Math.round(Math.random() * 20)
    // This is approximately 1/20 = 0.05 = 5%
    ORE_DROP_CHANCE: 0.05,  // 5%
    
    // From Game.js line 811
    // Math.round(Math.random() * 45) === Math.round(Math.random() * 45)
    // This is approximately 1/45 = 0.022 = 2.2%
    HEALTH_DROP_CHANCE: 0.022,  // 2.2%
    
    // From Game.js line 891
    HEALTH_PICKUP_VALUE: 30,
    
    // From Game.js line 110
    MAX_ORE_CAPACITY: 10,
    
    // Collectible collision radius - MEASURED from actual mesh geometry
    // Ore and health both have Box3 size (1.321, 0.201, 1.321)
    // Using max dimension / 2 = 1.321 / 2 = 0.661
    COLLECTIBLE_RADIUS: 0.661,
    
    // === WORLD BOUNDARIES ===
    // These are calculated from camera FOV in Game.js lines 65-69
    // With camera.fov=60, camera.position.y=30:
    // height = 2 * tan(30°) * 30 ≈ 34.64
    // width = height * aspect
    // These should be passed to GameCore constructor, not hardcoded
    // Default values for testing:
    WORLD_WIDTH_DEFAULT: 50,
    WORLD_HEIGHT_DEFAULT: 35,
    
    // === GAME LOOP ===
    FPS: 60,
    DELTA_TIME: 1.0 / 60,
    
    // === SCORING ===
    // Score per asteroid destroyed is in ASTEROID_TYPES above
    // Score increases asteroid spawn rate: 7 + score / 1000
};


